import { PathFinder, AlgorithmOptions } from '../core/PathFinder';
import { PathResult, AlgorithmStep } from '../core/PathResult';
import { BaseGrid } from '@/grid/BaseGrid';
import { Cell, CellState } from '@/grid/Cell';

export interface FlowFieldData {
  directions: Map<Cell, { dx: number; dy: number }>;
  costs: Map<Cell, number>;
}

export class FlowField extends PathFinder {
  readonly name = 'Flow Field';
  readonly description = 'Computes a vector field from goal using flood fill';

  private lastFlowFieldData: FlowFieldData | null = null;

  getFlowFieldData(): FlowFieldData | null {
    return this.lastFlowFieldData;
  }

  findPath(grid: BaseGrid, start: Cell, goal: Cell, options?: AlgorithmOptions): PathResult {
    const startTime = performance.now();
    const steps: AlgorithmStep[] = [];

    const generator = this.findPathStepByStep(grid, start, goal, options);

    let result = generator.next();
    while (!result.done) {
      steps.push(result.value);
      result = generator.next();
    }

    const executionTime = performance.now() - startTime;
    const pathResult = result.value;

    return new PathResult(
      pathResult.path,
      pathResult.nodesExplored,
      executionTime,
      pathResult.success,
      steps
    );
  }

  *findPathStepByStep(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    _options?: AlgorithmOptions
  ): Generator<AlgorithmStep, PathResult, undefined> {
    const startTime = performance.now();

    grid.reset();

    const costs = new Map<Cell, number>();
    const directions = new Map<Cell, { dx: number; dy: number }>();
    const queue: Cell[] = [];
    let nodesExplored = 0;

    costs.set(goal, 0);
    queue.push(goal);
    goal.state = CellState.OPEN;
    goal.gCost = 0;

    yield {
      type: 'open',
      cell: goal,
      cost: 0,
      openListSize: queue.length,
      closedListSize: nodesExplored,
      metadata: { flowField: true }
    };

    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentCost = costs.get(current)!;

      current.state = CellState.CLOSED;
      nodesExplored++;

      yield {
        type: 'close',
        cell: current,
        cost: currentCost,
        openListSize: queue.length,
        closedListSize: nodesExplored,
        metadata: { flowField: true }
      };

      const neighbors = grid.getNeighbors(current);

      for (const neighbor of neighbors) {
        const newCost = currentCost + grid.calculateDistance(current, neighbor) * neighbor.weight;

        if (!costs.has(neighbor) || newCost < costs.get(neighbor)!) {
          costs.set(neighbor, newCost);
          neighbor.gCost = newCost;

          directions.set(neighbor, {
            dx: current.col - neighbor.col,
            dy: current.row - neighbor.row
          });

          if (neighbor.state !== CellState.OPEN && neighbor.state !== CellState.CLOSED) {
            queue.push(neighbor);
            neighbor.state = CellState.OPEN;

            yield {
              type: 'open',
              cell: neighbor,
              cost: newCost,
              parent: current,
              openListSize: queue.length,
              closedListSize: nodesExplored,
              metadata: {
                flowField: true,
                direction: directions.get(neighbor)
              }
            };
          }
        }
      }
    }

    this.lastFlowFieldData = { directions, costs };

    const path = this.tracePath(start, goal, directions, costs);

    const executionTime = performance.now() - startTime;

    if (path.length > 0) {
      for (const cell of path) {
        if (!cell.isStart && !cell.isGoal) {
          yield {
            type: 'path_backtrack',
            cell,
            cost: costs.get(cell) || 0,
            openListSize: 0,
            closedListSize: nodesExplored
          };
        }
      }

      yield {
        type: 'path_found',
        cell: goal,
        cost: 0,
        openListSize: 0,
        closedListSize: nodesExplored
      };

      return new PathResult(path, nodesExplored, executionTime, true);
    }

    return PathResult.failure(nodesExplored, executionTime);
  }

  private tracePath(
    start: Cell,
    goal: Cell,
    directions: Map<Cell, { dx: number; dy: number }>,
    costs: Map<Cell, number>
  ): Cell[] {
    if (!costs.has(start)) {
      return [];
    }

    const cellByCoords = new Map<string, Cell>();
    for (const cell of costs.keys()) {
      cellByCoords.set(`${cell.row},${cell.col}`, cell);
    }

    const path: Cell[] = [start];
    let current = start;
    const visited = new Set<Cell>();
    visited.add(current);

    while (!current.equals(goal)) {
      const direction = directions.get(current);
      if (!direction) break;

      const nextRow = current.row + direction.dy;
      const nextCol = current.col + direction.dx;
      const nextCell = cellByCoords.get(`${nextRow},${nextCol}`);

      if (!nextCell || visited.has(nextCell)) break;

      visited.add(nextCell);
      path.push(nextCell);
      current = nextCell;

      if (path.length > costs.size + 1) break;
    }

    return path;
  }
}
